import React, { useEffect, useState } from 'react';
import Pagetitle from './component/Pagetitle';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useApiStore } from './hooks/ApiStoreContext';
import Swal from 'sweetalert2';

function Register() {
    const apiStore = useApiStore();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]); // Store users for uniqueness validation

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm({
        mode: "onBlur",
        defaultValues: {
            userId: 0,
            firstName: "",
            lastName: "",
            loginId: "",
            email: "",
            contact: "",
            password: "",
            roleId: "2",
            isActive: true,
        }
    });

    // Fetch users for uniqueness validation
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await apiStore.get("/Users");
                setUsers(response);
            } catch (error) {
                console.error("Error fetching users:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to load users data."
                });
            }
        };
        fetchUsers();
    }, [apiStore]);

    // Uniqueness validation functions
    const validateEmail = (email) => {
        if (!email) return true;
        const exists = users.some((user) => user.email.toLowerCase() === email.toLowerCase());
        return !exists || "Email is already taken.";
    };

    const validateContact = (contact) => {
        if (!contact) return true;
        const exists = users.some((user) => user.contact === contact);
        return !exists || "Contact number is already taken.";
    };

    const validateLoginId = (loginId) => {
        if (!loginId) return true;
        const exists = users.some((user) => user.loginId.toLowerCase() === loginId.toLowerCase());
        return !exists || "Login ID is already taken.";
    };

    const handleRegister = async (data) => {
        try {
            await apiStore.post('/Users/RegisterClient', data);
            resetRegister();
            await Swal.fire({
                title: "Success",
                text: "User registered successfully! Please Login.",
                icon: "success",
                confirmButtonText: "OK"
            });
            navigate("/login");
        } catch (error) {
            console.error("Error registering user:", error);
            let errorMessage = "There was an error registering the user.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            await Swal.fire({
                icon: "error",
                title: "Error",
                text: errorMessage
            });
        }
    };

    const resetRegister = () => {
        reset({
            userId: 0,
            firstName: "",
            lastName: "",
            loginId: "",
            email: "",
            contact: "",
            password: "",
            roleId: "2",
            isActive: true,
        });
    };

    return (
        <>
            <Pagetitle title="Register" />
            <section className="flat-spacing-10">
                <div className="container">
                    <div className="form-register-wrap">
                        <div className="flat-title align-items-start gap-0 mb_30 px-0">
                            <h5 className="mb_18">Register</h5>
                            <p className="text_black-2">Sign up for early Sale access plus tailored new arrivals, trends and promotions. To opt out, click unsubscribe in our emails</p>
                        </div>
                        <div>
                            <form className="" id="register-form" onSubmit={handleSubmit(handleRegister)}>
                                <div className="tf-field style-1 mb_15">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="text"
                                        id="firstName"
                                        {...register("firstName", { required: "First name is required." })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="firstName">First name *</label>
                                    <span className="text-xs text-danger">{errors.firstName?.message}</span>
                                </div>
                                <div className="tf-field style-1 mb_15">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="text"
                                        id="lastName"
                                        {...register("lastName", { required: "Last name is required." })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="lastName">Last name *</label>
                                    <span className="text-xs text-danger">{errors.lastName?.message}</span>
                                </div>
                                <div className="tf-field style-1 mb_15">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="text"
                                        id="loginId"
                                        {...register("loginId", {
                                            required: "Login ID is required.",
                                            minLength: { value: 6, message: "Login ID must be at least 6 characters." },
                                            maxLength: { value: 255, message: "Login ID cannot exceed 255 characters." },
                                            pattern: {
                                                value: /^[a-zA-Z0-9_-]+$/,
                                                message: "Login ID can only contain letters, numbers, underscores, or hyphens."
                                            },
                                            validate: validateLoginId
                                        })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="loginId">Login ID *</label>
                                    <span className="text-xs text-danger">{errors.loginId?.message}</span>
                                </div>
                                <div className="tf-field style-1 mb_15">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="email"
                                        id="email"
                                        {...register("email", {
                                            required: "Email is required.",
                                            pattern: {
                                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                                message: "Please enter a valid email address."
                                            },
                                            validate: validateEmail
                                        })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="email">Email *</label>
                                    <span className="text-xs text-danger">{errors.email?.message}</span>
                                </div>
                                <div className="tf-field style-1 mb_15">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="tel"
                                        id="contact"
                                        {...register("contact", {
                                            required: "Contact number is required.",
                                            pattern: { value: /^\d{10}$/, message: "Please enter a valid 10-digit contact number." },
                                            validate: validateContact
                                        })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="contact">Contact Number *</label>
                                    <span className="text-xs text-danger">{errors.contact?.message}</span>
                                </div>
                                <div className="tf-field style-1 mb_30">
                                    <input
                                        className="tf-field-input tf-input"
                                        placeholder=" "
                                        type="password"
                                        id="password"
                                        {...register("password", {
                                            required: "Password is required.",
                                            minLength: { value: 8, message: "Password should be minimum 8 characters in length." }
                                        })}
                                    />
                                    <label className="tf-field-label fw-4 text_black-2" htmlFor="password">Password *</label>
                                    <span className="text-xs text-danger">{errors.password?.message}</span>
                                </div>
                                <div className="mb_20">
                                    <button type="submit" className="tf-btn w-100 radius-3 btn-fill animate-hover-btn justify-content-center">Register</button>
                                </div>
                                <div className="text-center">
                                    <Link to="/login" className="tf-btn btn-line">Already have an account? Log in here<i className="icon icon-arrow1-top-left"></i></Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default Register;